import React from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Wind, EyeOff, ArrowUp, ArrowDown, ShieldAlert, Diamond } from 'lucide-react';

const EFFECT_TYPES = ['blur', 'sharpen', 'vignette', 'glow'];
const EFFECT_LABELS = { blur: 'Blur', sharpen: 'Sharpen', vignette: 'Vignette', glow: 'Glow' };
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const SingleSlider = ({ value, onChange, min = 0, max = 100, step = 1, testId }) => (
  <div className="flex items-center gap-3">
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="flex-1" data-testid={testId} />
    <span className="text-xs text-white w-10 text-right">{Math.round(value)}</span>
  </div>
);

/**
 * Visual effects (blur/sharpen/vignette/glow — each orderable,
 * enable/disable-able and keyframeable) and manual privacy blur regions,
 * for the selected VIDEO item. `sharpen` has no faithful browser-preview
 * technique, so it is flagged "render-only" rather than faking a live
 * preview. Automatic face tracking is NOT implemented — no real
 * media-analysis service exists in this codebase — so it stays an
 * honest pending-integration toggle; manual placement/keyframing works.
 */
export function EditorEffectsPanel({ item, localTime, onEffectsChange, onBlurRegionsChange }) {
  if (item.item_type !== 'video') return null;
  const effects = [...(item.effects || [])].sort((a, b) => a.order - b.order);
  const regions = item.blur_regions || [];

  const addEffect = (type) => onEffectsChange([...effects, { id: newId(), type, enabled: true, intensity_percent: 50, order: effects.length, keyframes: [] }]);
  const updateEffect = (id, partial) => onEffectsChange(effects.map((e) => (e.id === id ? { ...e, ...partial } : e)));
  const removeEffect = (id) => onEffectsChange(effects.filter((e) => e.id !== id).map((e, i) => ({ ...e, order: i })));
  const moveEffect = (id, dir) => {
    const idx = effects.findIndex((e) => e.id === id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= effects.length) return;
    const next = [...effects];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    onEffectsChange(next.map((e, i) => ({ ...e, order: i })));
  };
  const addEffectKeyframe = (id) => {
    const effect = effects.find((e) => e.id === id);
    if (!effect) return;
    const kf = { id: newId(), time_seconds: Math.max(0, localTime), intensity_percent: effect.intensity_percent };
    updateEffect(id, { keyframes: [...(effect.keyframes || []), kf].sort((a, b) => a.time_seconds - b.time_seconds) });
  };
  const removeEffectKeyframe = (effectId, kfId) => {
    const effect = effects.find((e) => e.id === effectId);
    if (!effect) return;
    updateEffect(effectId, { keyframes: (effect.keyframes || []).filter((k) => k.id !== kfId) });
  };

  const addBlurRegion = () => onBlurRegionsChange([...regions, { id: newId(), x_percent: 40, y_percent: 40, width_percent: 20, height_percent: 20, blur_intensity_percent: 80, keyframes: [], auto_tracking_enabled: false, auto_tracking_status: 'not_requested' }]);
  const updateRegion = (id, partial) => onBlurRegionsChange(regions.map((r) => (r.id === id ? { ...r, ...partial } : r)));
  const removeRegion = (id) => onBlurRegionsChange(regions.filter((r) => r.id !== id));
  const addRegionKeyframe = (id) => {
    const region = regions.find((r) => r.id === id);
    if (!region) return;
    const kf = { id: newId(), time_seconds: Math.max(0, localTime), x_percent: region.x_percent, y_percent: region.y_percent, width_percent: region.width_percent, height_percent: region.height_percent };
    updateRegion(id, { keyframes: [...(region.keyframes || []), kf].sort((a, b) => a.time_seconds - b.time_seconds) });
  };
  const removeRegionKeyframe = (regionId, kfId) => {
    const region = regions.find((r) => r.id === regionId);
    if (!region) return;
    updateRegion(regionId, { keyframes: (region.keyframes || []).filter((k) => k.id !== kfId) });
  };

  return (
    <div className="space-y-4 pt-2 border-t border-white/5" data-testid="effects-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide flex items-center gap-1.5"><Wind className="h-3 w-3" />Visual Effects</p>
        <Select onValueChange={addEffect}>
          <SelectTrigger className="h-6 w-28 text-[10px] bg-[#1C1F26] border-white/10 text-white" data-testid="effect-add-select"><SelectValue placeholder="+ Add effect" /></SelectTrigger>
          <SelectContent>{EFFECT_TYPES.map((t) => <SelectItem key={t} value={t}>{EFFECT_LABELS[t]}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {effects.length === 0 && <p className="text-[11px] text-[#555E6E]" data-testid="effects-list-empty">No effects applied yet.</p>}

      {effects.map((effect, idx) => (
        <div key={effect.id} className="p-2.5 rounded-lg bg-white/[0.03] border border-white/10 space-y-2" data-testid={`effect-row-${effect.id}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs text-white font-medium">{EFFECT_LABELS[effect.type]}</span>
            <div className="flex items-center gap-1.5">
              <button disabled={idx === 0} onClick={() => moveEffect(effect.id, -1)} className="text-[#8A93A6] hover:text-white disabled:opacity-30" data-testid={`effect-up-${effect.id}`}><ArrowUp className="h-3 w-3" /></button>
              <button disabled={idx === effects.length - 1} onClick={() => moveEffect(effect.id, 1)} className="text-[#8A93A6] hover:text-white disabled:opacity-30" data-testid={`effect-down-${effect.id}`}><ArrowDown className="h-3 w-3" /></button>
              <Switch checked={effect.enabled} onCheckedChange={(v) => updateEffect(effect.id, { enabled: v })} data-testid={`effect-enable-${effect.id}`} />
              <button onClick={() => removeEffect(effect.id)} className="text-red-400 hover:text-red-300" data-testid={`effect-remove-${effect.id}`}><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
          {effect.type === 'sharpen' && (
            <p className="flex items-center gap-1 text-[10px] text-amber-400/80" data-testid={`effect-render-only-${effect.id}`}><EyeOff className="h-3 w-3" />Render-only — no faithful live preview in-browser.</p>
          )}
          <SingleSlider value={effect.intensity_percent} onChange={(v) => updateEffect(effect.id, { intensity_percent: v })} testId={`effect-intensity-${effect.id}`} />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-[#555E6E] flex items-center gap-1"><Diamond className="h-2.5 w-2.5" />{(effect.keyframes || []).length} keyframe(s)</span>
            <Button size="sm" variant="outline" className="h-6 text-[10px] border-white/10" onClick={() => addEffectKeyframe(effect.id)} data-testid={`effect-add-keyframe-${effect.id}`}>+ At Playhead</Button>
          </div>
          {(effect.keyframes || []).length > 0 && (
            <div className="space-y-1">
              {effect.keyframes.map((kf) => (
                <div key={kf.id} className="flex items-center justify-between px-2 py-0.5 rounded bg-white/5 text-[10px] text-white" data-testid={`effect-keyframe-row-${kf.id}`}>
                  <span>{kf.time_seconds.toFixed(1)}s — {Math.round(kf.intensity_percent)}%</span>
                  <button onClick={() => removeEffectKeyframe(effect.id, kf.id)} className="text-red-400 hover:text-red-300"><Trash2 className="h-2.5 w-2.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <div className="pt-2 border-t border-white/5 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#555E6E] uppercase tracking-wide flex items-center gap-1.5"><ShieldAlert className="h-3 w-3" />Privacy Blur</p>
          <Button size="sm" variant="outline" className="h-6 text-[10px] border-white/10" onClick={addBlurRegion} data-testid="blur-region-add-button"><Plus className="h-3 w-3 mr-1" />Region</Button>
        </div>
        {regions.length === 0 && <p className="text-[11px] text-[#555E6E]" data-testid="blur-regions-empty">No blur regions yet — add one to manually cover a face or detail.</p>}
        {regions.map((region) => (
          <div key={region.id} className="p-2.5 rounded-lg bg-white/[0.03] border border-white/10 space-y-2" data-testid={`blur-region-row-${region.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs text-white">Blur Region</span>
              <button onClick={() => removeRegion(region.id)} className="text-red-400 hover:text-red-300" data-testid={`blur-region-remove-${region.id}`}><Trash2 className="h-3 w-3" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <SingleSlider value={region.x_percent} min={0} max={Math.max(0, 100 - region.width_percent)} onChange={(v) => updateRegion(region.id, { x_percent: v })} testId={`blur-region-x-${region.id}`} />
              <SingleSlider value={region.y_percent} min={0} max={Math.max(0, 100 - region.height_percent)} onChange={(v) => updateRegion(region.id, { y_percent: v })} testId={`blur-region-y-${region.id}`} />
              <SingleSlider value={region.width_percent} min={2} max={Math.max(2, 100 - region.x_percent)} onChange={(v) => updateRegion(region.id, { width_percent: v })} testId={`blur-region-width-${region.id}`} />
              <SingleSlider value={region.height_percent} min={2} max={Math.max(2, 100 - region.y_percent)} onChange={(v) => updateRegion(region.id, { height_percent: v })} testId={`blur-region-height-${region.id}`} />
            </div>
            <label className="text-[10px] text-[#8A93A6]">Blur Intensity</label>
            <SingleSlider value={region.blur_intensity_percent} onChange={(v) => updateRegion(region.id, { blur_intensity_percent: v })} testId={`blur-region-intensity-${region.id}`} />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-[#555E6E]">{(region.keyframes || []).length} keyframe(s)</span>
              <Button size="sm" variant="outline" className="h-6 text-[10px] border-white/10" onClick={() => addRegionKeyframe(region.id)} data-testid={`blur-region-add-keyframe-${region.id}`}>+ At Playhead</Button>
            </div>
            {(region.keyframes || []).length > 0 && (
              <div className="space-y-1">
                {region.keyframes.map((kf) => (
                  <div key={kf.id} className="flex items-center justify-between px-2 py-0.5 rounded bg-white/5 text-[10px] text-white" data-testid={`blur-region-keyframe-row-${kf.id}`}>
                    <span>{kf.time_seconds.toFixed(1)}s</span>
                    <button onClick={() => removeRegionKeyframe(region.id, kf.id)} className="text-red-400 hover:text-red-300"><Trash2 className="h-2.5 w-2.5" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between pt-1">
              <span className="text-[10px] text-[#8A93A6]">Auto face-tracking</span>
              <Switch
                checked={!!region.auto_tracking_enabled}
                onCheckedChange={(v) => updateRegion(region.id, { auto_tracking_enabled: v, auto_tracking_status: v ? 'pending_analysis' : 'not_requested' })}
                data-testid={`blur-region-autotrack-${region.id}`}
              />
            </div>
            {region.auto_tracking_enabled && (
              <p className="flex items-center gap-1 text-[10px] text-amber-400/80" data-testid={`blur-region-autotrack-status-${region.id}`}>
                <EyeOff className="h-3 w-3" />Pending integration — no face-tracking analysis service connected yet. Position/keyframe the region manually until then.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
